import { useState, useEffect } from "react"
import { useParams, useNavigate, Link } from "react-router"
import {
  useGetOrderDetailQuery,
  useSaveOrderMutation,
} from "@apis/orders/useOrders"
import { useGetOfferQuery } from "@apis/offers/useOffers"
import { Loader2 } from "lucide-react"
import { EditOrderForm } from "@components/orders/EditOrderForm"
import { useForm } from "react-hook-form"
import { Button } from "@components/ui/button"
import { useDrawer } from "@contexts/DrawerContext"
import { UpdateOrderStatus } from "@components/orders/UpdateOrderStatusForm"
import { toast } from "sonner"
import ErrorMessage from "@components/ErrorMessage"
import type { AxiosError } from "axios"
import type { IOrderPayload } from "@commonTypes/order"
import { AccountDetailsButton } from "@components/AccountDetailsButton"
import { isDuplicateAccountError } from "@apis/orders/utils"
import { Alert, AlertDescription } from "@components/ui/alert"

type TEditOrderForm = IOrderPayload & {
  market_code: string
}

export const EditOrderPage = () => {
  const { order_id } = useParams()
  const navigate = useNavigate()
  const order_id_number = parseInt(order_id as string)
  const {
    data: order_data,
    isPending,
    isError,
    error,
  } = useGetOrderDetailQuery(order_id_number)

  const { data: offer_data } = useGetOfferQuery(order_data?.offer_code)

  const [formInitialized, setFormInitialized] = useState(false)

  const { mutate, isPending: isMutating } = useSaveOrderMutation()

  const { openDrawer, closeDrawer } = useDrawer()

  const form = useForm<TEditOrderForm>({
    mode: "onTouched",
  })

  useEffect(() => {
    if (order_data && !formInitialized) {
      form.reset({
        ...order_data.order_payload,
        service_address_id: null,
        mailing_address_id: null,
        market_code:
          // When an order is created through the Site, for some reason the market_code
          // is not being saved in order.market_code, but is being saved inside the order_payload.
          // We could fallback to order_payload.market_code until we figure out why this is happening and fix it.
          order_data.market_code,
        skip_duplicate_account_validation: false,
        merge_into_existing_account: false,
      })
      setFormInitialized(true)
    }
  }, [order_data, form, formInitialized])

  const handleSubmit = (formData: TEditOrderForm & { offer_code: string }) => {
    const { offer_code, account_number, ...orderPayload } = formData

    const normalizedAccountNumber =
      account_number?.trim() === "" ? null : account_number

    mutate(
      {
        body: {
          offer_code,
          account_number: normalizedAccountNumber,
          ...orderPayload,
          conversion_medium: "INSPIRE_CRM",
        },
      },
      {
        onSuccess: () => {
          toast.success("Order successfully Updated!")
          navigate(-1)
        },
        onError: (error: string | AxiosError) =>
          toast.error(`Error updating order! ${error}`),
      },
    )
  }

  const handleUpdateStatus = () => {
    closeDrawer()
    navigate("/ops/orders/failed")
  }

  if (isPending) {
    return (
      <div className="tw:flex tw:justify-center tw:py-10">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="tw:py-4 tw:text-red-500">
        Error loading the Failed Order. {error.message}
      </div>
    )
  }

  const handleUpdateOrder = () => {
    openDrawer(
      <UpdateOrderStatus
        order_id={order_id_number}
        onCancel={closeDrawer}
        onSubmitSuccess={handleUpdateStatus}
      />,
      "Update Order Status",
    )
  }

  if (!order_data) return null

  return (
    <div className="tw:mx-auto tw:flex tw:flex-col tw:gap-6 tw:py-6">
      <div className="tw:flex tw:items-center tw:justify-between">
        <h1 className="tw:mb-4 tw:text-2xl tw:font-bold">
          Edit Order #{order_id}
        </h1>
        <AccountDetailsButton
          membership_profile_id={order_data.account_membership_profile_id}
          account_id={order_data.account_account_id}
          market_code={order_data.market_code}
          account_number={order_data.account_number}
        />
      </div>
      {order_data.error_messages?.map((error, index) => (
        <div key={index} className="tw:flex tw:flex-col tw:gap-2">
          <ErrorMessage
            title={error.failed_attribute}
            message={error.message}
            variant="destructive-secondary"
          />
          {isDuplicateAccountError(error) &&
            error.meta?.duplicate_accounts?.length > 0 && (
              <Alert>
                <AlertDescription className="tw:flex tw:items-center tw:gap-3">
                  <span>Duplicate Accounts:</span>
                  <span className="tw:flex tw:gap-2">
                    {error.meta?.duplicate_accounts?.map((account) => (
                      <Button
                        key={account.account_id}
                        variant="link"
                        asChild
                        className="tw:p-0"
                      >
                        <Link
                          to={`/profiles/${account.membership_profile_id}/accounts/${account.account_id}`}
                          target="_blank"
                        >
                          {account.account_id}
                        </Link>
                      </Button>
                    ))}
                  </span>
                </AlertDescription>
              </Alert>
            )}
        </div>
      ))}

      <EditOrderForm form={form} offer={offer_data} order_data={order_data} />
      <div className="tw:flex tw:justify-between">
        <div className="tw:flex tw:gap-2">
          <Button
            type="button"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={!form.formState.isValid || isMutating}
          >
            {isMutating ? "Processing..." : "Submit Order"}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Return to List
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => handleUpdateOrder()}
        >
          Update Status
        </Button>
      </div>
    </div>
  )
}
